import type { OfferingV2Response } from '../../types/offeringsV2';
import type {
  PackageItemRequest, PackagePreviewRequest, PackagePricingMode, PackageRequest, PackageResponse, PackageStatus,
} from '../../types/packages';
import { daysBetween, hasContactDetails } from '../offers/offerUtils';

// ── Limits (requirements §4 Limits, §6 R2/R6; design §3 PackageRequest) ──
export const NAME_MAX = 60;
export const DESCRIPTION_MAX = 1000;
export const ITEMS_MIN = 2;
export const ITEMS_MAX = 6;
export const FIXED_VALUES_MAX = 10;
export const PERCENT_MIN = 5;
export const PERCENT_MAX = 70;
export const MAX_WINDOW_DAYS = 90;

/** Running statuses: edits apply in place (Q3 = B), drag order applies, pause/resume/end allowed. */
export const RUNNING_STATUSES: PackageStatus[] = ['SCHEDULED', 'LIVE', 'PAUSED'];
/** Statuses the partner edits in place and (re)submits. */
export const SUBMITTABLE_STATUSES: PackageStatus[] = ['DRAFT', 'REJECTED', 'SUSPENDED'];

// ── Form ──
export interface PackageFormItem {
  offeringId: string;
  /** Values fixed by the partner; a variation with none of its values here is the customer's pick. */
  fixedValueIds: string[];
}

export interface PackageForm {
  nameEn: string;
  nameAr: string;
  descriptionEn: string;
  descriptionAr: string;
  coverStorageKey: string | null;
  coverUrl: string | null;
  items: PackageFormItem[];
  packagePrice: string;
  discountPercent: string;
  startsAt: string;
  endsAt: string;
}

export type PackageItemField = `item:${string}`;
export type PackageField =
  | 'nameEn' | 'nameAr' | 'descriptionEn' | 'descriptionAr' | 'cover' | 'items'
  | 'packagePrice' | 'discountPercent' | 'startsAt' | 'endsAt' | PackageItemField;
export type PackageFieldErrors = Partial<Record<PackageField, string>>;

export const packageItemField = (offeringId: string): PackageItemField => `item:${offeringId}`;

export function emptyPackageForm(): PackageForm {
  return {
    nameEn: '', nameAr: '', descriptionEn: '', descriptionAr: '', coverStorageKey: null, coverUrl: null,
    items: [], packagePrice: '', discountPercent: '', startsAt: '', endsAt: '',
  };
}

const numStr = (n: number | null | undefined) => (n == null ? '' : String(n));

export function formFromPackage(p: PackageResponse): PackageForm {
  return {
    nameEn: p.nameEn ?? '',
    nameAr: p.nameAr ?? '',
    descriptionEn: p.descriptionEn ?? '',
    descriptionAr: p.descriptionAr ?? '',
    coverStorageKey: p.cover?.storageKey ?? null,
    coverUrl: p.cover?.downloadUrl ?? null,
    items: (p.items ?? []).map((i) => ({ offeringId: i.offeringId, fixedValueIds: (i.fixedChoices ?? []).map((c) => c.valueId) })),
    packagePrice: numStr(p.packagePrice),
    discountPercent: numStr(p.discountPercent),
    startsAt: (p.startsAt ?? '').slice(0, 10),
    endsAt: (p.endsAt ?? '').slice(0, 10),
  };
}

const optText = (s: string) => (s.trim() ? s.trim() : null);
const optNum = (s: string) => (s.trim() === '' || !Number.isFinite(Number(s)) ? null : Number(s));
const itemsToRequest = (items: PackageFormItem[]): PackageItemRequest[] =>
  items.map((i) => ({ offeringId: i.offeringId, fixedVariationValueIds: [...i.fixedValueIds] }));

/** Only the value of the derived mode is sent; the other is null (the server requires the matching one). */
export function packageFormToRequest(f: PackageForm, mode: PackagePricingMode, version?: number): PackageRequest {
  const req: PackageRequest = {
    nameEn: f.nameEn.trim(),
    nameAr: optText(f.nameAr),
    descriptionEn: optText(f.descriptionEn),
    descriptionAr: optText(f.descriptionAr),
    coverStorageKey: f.coverStorageKey,
    items: itemsToRequest(f.items),
    packagePrice: mode === 'FIXED' ? optNum(f.packagePrice) : null,
    discountPercent: mode === 'PERCENT' ? optNum(f.discountPercent) : null,
    startsAt: f.startsAt || null,
    endsAt: f.endsAt || null,
  };
  if (version != null) req.version = version;
  return req;
}

/** The preview sends both typed values; the server derives the mode and uses the matching one. */
export function packageFormToPreviewRequest(f: PackageForm): PackagePreviewRequest {
  return { items: itemsToRequest(f.items), packagePrice: optNum(f.packagePrice), discountPercent: optNum(f.discountPercent) };
}

// ── Eligibility (picker) ──
export type IneligibleReason = 'BLOCKED' | 'INACTIVE' | 'PENDING_APPROVAL' | 'REJECTED';

export function ineligibleReasonOf(o: Pick<OfferingV2Response, 'status' | 'blockedAt'>): IneligibleReason | null {
  if (o.blockedAt) return 'BLOCKED';
  if (o.status === 'ACTIVE') return null;
  return o.status === 'INACTIVE' || o.status === 'PENDING_APPROVAL' || o.status === 'REJECTED' ? o.status : 'INACTIVE';
}

export const isOfferingEligible = (o: Pick<OfferingV2Response, 'status' | 'blockedAt'>) => ineligibleReasonOf(o) == null;

// ── Choices ──
type OfferingShape = Pick<OfferingV2Response, 'isCarSpecific' | 'variations'>;

/** True when every variation of the offering has at least one fixed value. */
export function itemFullyFixed(item: PackageFormItem, o: OfferingShape): boolean {
  const fixed = new Set(item.fixedValueIds);
  return (o.variations ?? []).every((v) => (v.values ?? []).some((val) => fixed.has(val.id)));
}

/**
 * D2 client mirror (display only; the preview response wins): FIXED when every item has every
 * variation fixed and no item is car-specific, PERCENT otherwise. Unknown offerings are skipped.
 */
export function derivePricingMode(items: PackageFormItem[], catalogue: Map<string, OfferingShape>): PackagePricingMode {
  for (const item of items) {
    const o = catalogue.get(item.offeringId);
    if (!o) continue;
    if (o.isCarSpecific) return 'PERCENT';
    if (!itemFullyFixed(item, o)) return 'PERCENT';
  }
  return 'FIXED';
}

/** Replaces the fixed values of one variation (empty = the customer picks). */
export function setVariationValues(item: PackageFormItem, o: OfferingShape, variationId: string, valueIds: string[]): PackageFormItem {
  const variation = (o.variations ?? []).find((v) => v.id === variationId);
  const own = new Set((variation?.values ?? []).map((v) => v.id));
  return { ...item, fixedValueIds: [...item.fixedValueIds.filter((id) => !own.has(id)), ...valueIds] };
}

// ── Money ──
/** D12: floor so the tile never overstates the saving. toFixed(6) absorbs binary noise (57 / 100 × 100 = 56.99999999999999). */
export function savingPercent(saving: number | null | undefined, total: number | null | undefined): number {
  if (saving == null || total == null || !(total > 0) || !(saving > 0)) return 0;
  return Math.floor(Number(((saving / total) * 100).toFixed(6)));
}

/** FIXED preview: the saving in AED and %, or null when the price is not a usable number below the total. */
export function fixedSaving(total: number | null | undefined, priceInput: string): { amount: number; pct: number } | null {
  const price = Number(priceInput);
  if (total == null || priceInput.trim() === '' || !Number.isFinite(price) || price <= 0 || price >= total) return null;
  const amount = Math.round((total - price) * 100) / 100;
  return { amount, pct: savingPercent(amount, total) };
}

// ── Window (D9: offers calendar rule, both dates optional) ──
export interface WindowContext {
  today: string;
  /** A running package whose start is unchanged keeps its (past) start date. */
  startLocked: boolean;
}

export function validateWindow(startsAt: string, endsAt: string, ctx: WindowContext): PackageFieldErrors {
  const e: PackageFieldErrors = {};
  if (startsAt && !ctx.startLocked && startsAt < ctx.today) e.startsAt = 'packages.err.startPast';
  if (endsAt) {
    if (startsAt && endsAt < startsAt) e.endsAt = 'packages.err.endBeforeStart';
    else if (!startsAt && endsAt < ctx.today) e.endsAt = 'packages.err.endPast';
    else if (startsAt && daysBetween(startsAt, endsAt) > MAX_WINDOW_DAYS) e.endsAt = 'packages.err.window';
  }
  return e;
}

// ── Whole-form validation (translation keys per field) ──
export function validateTexts(f: PackageForm): PackageFieldErrors {
  const e: PackageFieldErrors = {};
  if (!f.nameEn.trim()) e.nameEn = 'packages.err.nameRequired';
  const checks: [keyof PackageForm & PackageField, number, string][] = [
    ['nameEn', NAME_MAX, 'packages.err.nameTooLong'], ['nameAr', NAME_MAX, 'packages.err.nameTooLong'],
    ['descriptionEn', DESCRIPTION_MAX, 'packages.err.descriptionTooLong'], ['descriptionAr', DESCRIPTION_MAX, 'packages.err.descriptionTooLong'],
  ];
  for (const [field, max, key] of checks) {
    const v = String(f[field] ?? '');
    if (e[field]) continue;
    if (v.trim().length > max) e[field] = key;
    else if (hasContactDetails(v)) e[field] = 'packages.err.contactDetails';
  }
  return e;
}

export function validatePrice(f: PackageForm, mode: PackagePricingMode, originalTotal: number | null): PackageFieldErrors {
  if (mode === 'FIXED') {
    const price = Number(f.packagePrice);
    if (f.packagePrice.trim() === '' || !Number.isFinite(price) || price <= 0) return { packagePrice: 'packages.err.priceRequired' };
    if (originalTotal != null && price >= originalTotal) return { packagePrice: 'packages.err.priceNotBelowTotal' };
    return {};
  }
  const pct = Number(f.discountPercent);
  if (f.discountPercent.trim() === '' || !Number.isInteger(pct) || pct < PERCENT_MIN || pct > PERCENT_MAX) {
    return { discountPercent: 'packages.err.percentRange' };
  }
  return {};
}

export function validateItemsCount(f: PackageForm): PackageFieldErrors {
  return f.items.length < ITEMS_MIN || f.items.length > ITEMS_MAX ? { items: 'packages.err.itemsCount' } : {};
}

// ── List helpers ──
export type PackageRowAction = 'pause' | 'resume' | 'end' | 'archive' | 'withdraw';

export function packageStatusChipClass(status: PackageStatus): string {
  return `offer-status offer-status--${status.toLowerCase().replace('_', '-')}`;
}

/** Row actions per design §3 transitions (archive DRAFT|REJECTED|ENDED; pause LIVE|SCHEDULED). */
export function packageRowActions(p: Pick<PackageResponse, 'status'>, archived = false): { edit: boolean; actions: PackageRowAction[] } {
  if (archived) return { edit: false, actions: [] };
  switch (p.status) {
    case 'DRAFT':
    case 'REJECTED':
      return { edit: true, actions: ['archive'] };
    case 'SUSPENDED':
      return { edit: true, actions: [] };
    case 'PENDING_APPROVAL':
      return { edit: false, actions: ['withdraw'] };
    case 'SCHEDULED':
    case 'LIVE':
      return { edit: true, actions: ['pause', 'end'] };
    case 'PAUSED':
      return { edit: true, actions: ['resume', 'end'] };
    case 'ENDED':
      return { edit: false, actions: ['archive'] };
    default:
      return { edit: false, actions: [] };
  }
}

/** Running packages first, in their display order; the rest keep the server's order. */
export function sortForTable(list: PackageResponse[]): PackageResponse[] {
  const running = list.filter((p) => RUNNING_STATUSES.includes(p.status))
    .sort((a, b) => (a.displayOrder ?? Number.MAX_SAFE_INTEGER) - (b.displayOrder ?? Number.MAX_SAFE_INTEGER));
  return [...running, ...list.filter((p) => !RUNNING_STATUSES.includes(p.status))];
}

/** Moves `id` to the 1-based `position` inside `ids`; returns null when nothing moves. */
export function moveId(ids: string[], id: string, position: number): string[] | null {
  const from = ids.indexOf(id);
  const to = Math.min(Math.max(position, 1), ids.length) - 1;
  if (from < 0 || from === to) return null;
  const next = [...ids];
  next.splice(to, 0, next.splice(from, 1)[0]);
  return next;
}
